import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "../utils/Modal/Modal";
import {
  AdminActionButton,
  AdminActionsContainer,
  CategoryContainer,
  CategoryItem,
  CategoryText,
  Content,
  Header,
  IconWrapper,
  ModalContainer,
  Tab,
  TabsContainer,
  TabText,
  Title,
  RightColumn,
  SectionLabel,
  RightContent,
  RightContentOverlay,
  StyledVideo,
  StyledImage,
  Container,
  GenerateContainer,
  InfoIconWrapper,
  TooltipContainer,
  TooltipText,
  GenerateContainerMobile,
  StyledIconWrapper,
  MobileCheckboxRow,
  MobileToggleRow,
  MobileToggleTrack,
  MobileToggleThumb,
  NudifyText,
  RowIcons,
  FavoriteIconWrapper,
  FavoriteHeartOutline,
  FavoriteHeartFilled,
  FavoriteBurst,
  EmptyDiv,
  GenerateLoader,
  GenerateLoaderDots,
  GenerateLoaderSparkle,
  // ConfirmPermissionContainer,
  // ConfirmPermissionText,
} from "./GenerateCollectionModal.styles";

import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as HeartOutlineIcon } from "../../assets/icons/favorites.svg";
import { ReactComponent as HeartFilledIcon } from "../../assets/icons/filledFavorites.svg";

import { ReactComponent as ImageIcon } from "../../assets/icons/image.svg";
import { ReactComponent as VideoRecorderIcon } from "../../assets/icons/video-recorder.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/info-circle.svg";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../store/slices/user/slice";
import {
  selectGenerateCollection,
  selectHideCollectionPending,
  selectHideCollectionVipPending,
  selectSelectedCollection,
  selectToggleToFavoriteLoading,
} from "../../store/slices/model/slice";
import {
  generateCollection,
  hideCollection,
  hideCollectionVip,
  toggleToFavorite,
} from "../../store/slices/model/asyncThunks";
import ExtendVideo from "../ExtendVideo/ExtendVideo";
import ImageToVideo from "../ImageToVideo/ImageToVideo";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { formatModelName, isImageOutput } from "../../utils/utils";
import {
  MEDIA_TYPE,
  NUDIFY_PRICE,
  VIDEO_DURATION_PRICE,
  VIDEO_RESOLUTION_PRICE,
  DEFAULT_VIDEO_DURATION,
  DEFAULT_VIDEO_RESOLUTION,
  ALLOWED_HIDE_COLLECTION_USER_IDS,
  UPSCALE_PRICE,
} from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import CropModal from "../CropModal/CropModal";
import EnterPromptModal from "../EnterPromptModal/EnterPromptModal";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import LottieIcon from "../LottieIcon/LottieIcon";
import settingsAnimation from "../../assets/animations/settings.json";
// import checkboxAnimation from "../../assets/animations/checkBox.json";
import { toast } from "sonner";
import { useMediaQuery } from "react-responsive";
import FlyToCollection from "../FlyToCollection/FlyToCollection";
import { triggerCollectionTabAnimation } from "../../store/slices/common/slice";
import ExplosionButton from "../ExplosionButton/ExplosionButton";
import { GenerateDrop } from "../CreateInputBar/CreateInputBar.styles";
import GenerationToast from "../utils/GenerationToast";
// import TermsCheckboxComponent from "../TermsCheckboxComponent/TermsCheckboxComponent";

const MODAL_CONTENT = {
  IMAGE_TO_VIDEO: "IMAGE_TO_VIDEO",
  EXTEND_VIDEO: "EXTEND_VIDEO",
};

// const MEDIA_PERMISSION_KEY = "mediaPermissionConfirmed";

const hasVipTag = (collection) =>
  Array.isArray(collection?.tags) &&
  collection.tags.some((tag) =>
    typeof tag === "string" ? tag === "VIP" : tag?.name === "VIP",
  );

const MemoizedRightContent = React.memo(({ output, input }) => {
  if (!output || !input) return null;

  return (
    <RightContent>
      {output.type === MEDIA_TYPE.VIDEO ? (
        <StyledVideo
          autoplay
          src={output.video.compressedUrl || output.video.url}
          imageSrc={input.image.resizedImage.url || input.image.url}
        />
      ) : (
        <StyledImage src={output.image.url} />
      )}
      <RightContentOverlay />
    </RightContent>
  );
});

const InfoTooltip = ({ selectedTab, collection, tooltipText, isMobile }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);
  const infoIconWrapperRef = useRef(null);

  useEffect(() => {
    if (!showTooltip) return;

    const handleClickOutside = (event) => {
      if (
        !tooltipRef.current?.contains(event.target) &&
        !infoIconWrapperRef.current?.contains(event.target)
      ) {
        setShowTooltip(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showTooltip]);

  return (
    <InfoIconWrapper
      ref={infoIconWrapperRef}
      $active={collection?.description && collection.description !== ""}
      onMouseEnter={() => {
        if (!isMobile) {
          setShowTooltip(true);
        }
      }}
      onMouseLeave={() => {
        if (!isMobile) {
          setShowTooltip(false);
        }
      }}
      onClick={() => {
        if (isMobile) {
          setShowTooltip((prev) => !prev);
        }
      }}
    >
      <InfoIcon width={20} height={20} />
      {showTooltip && (
        <TooltipContainer ref={tooltipRef}>
          {selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO ? (
            <>
              <TooltipText>
                The uploaded image will be used as the first frame of the
                generated video.
              </TooltipText>
              <TooltipText>
                For best results, match the original image. The AI follows basic
                physics - objects not in the input image are unlikely to appear
                in the generated video.
              </TooltipText>
            </>
          ) : (
            <TooltipText>{tooltipText}</TooltipText>
          )}

          {collection?.description && collection.description !== "" && (
            <>
              <TooltipText $light>Tips</TooltipText>
              <TooltipText>{collection.description}</TooltipText>
            </>
          )}
        </TooltipContainer>
      )}
    </InfoIconWrapper>
  );
};

const GenerateCollectionModal = ({ isOpen, onClose, initialPrompt = "" }) => {
  useLockBodyScroll(isOpen);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const collection = useSelector(selectSelectedCollection);
  const loading = useSelector(selectToggleToFavoriteLoading);
  const { loading: generateLoading } = useSelector(selectGenerateCollection);
  const hideCollectionPending = useSelector(selectHideCollectionPending);
  const hideCollectionVipPending = useSelector(selectHideCollectionVipPending);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const uploadedImageRef = useRef(null);
  const uploadedVideoRef = useRef(null);

  const isLoading =
    loading ||
    generateLoading ||
    hideCollectionPending ||
    hideCollectionVipPending;
  const userId = user?._id || user?.id;
  const canManageCollectionVisibility =
    !!userId && ALLOWED_HIDE_COLLECTION_USER_IDS.includes(userId);

  const [selectedTab, setSelectedTab] = useState(MODAL_CONTENT.IMAGE_TO_VIDEO);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [nudifyEnabled, setNudifyEnabled] = useState(false);
  const [upscaleEnabled, setUpscaleEnabled] = useState(false);

  // const [confirmPermission, setConfirmPermission] = useState(
  //   localStorage.getItem(MEDIA_PERMISSION_KEY) === "true",
  // );
  // const [permissionError, setPermissionError] = useState(false);

  // const [shouldShake, setShouldShake] = useState(false);
  // const [showOutline, setShowOutline] = useState(false);
  // const [agreeTos, setAgreeTos] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [showPrompts, setShowPrompts] = useState(false);

  const [customSeed, setCustomSeed] = useState(
    Math.floor(Math.random() * 10000000) + 1,
  );
  const [isExploding, setIsExploding] = useState(false);
  const [isCollectionHiddenLocally, setIsCollectionHiddenLocally] =
    useState(false);
  const [isVipTagHiddenLocally, setIsVipTagHiddenLocally] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);

  const [isOpenCropModal, setIsOpenCropModal] = useState(false);
  const [isEnterPromptModalOpen, setIsEnterPromptModalOpen] = useState(false);

  const [flyAnimation, setFlyAnimation] = useState({
    isActive: false,
    imageUrl: null,
    startPosition: null,
    imageDimensions: null,
    onComplete: null,
  });

  const [videoResolution, setVideoResolution] = useState(
    localStorage.getItem("videoResolution") ?? DEFAULT_VIDEO_RESOLUTION,
  );

  const [videoDuration, setVideoDuration] = useState(
    Number(localStorage.getItem("videoDuration") ?? DEFAULT_VIDEO_DURATION),
  );

  const collectionPrice = useMemo(() => {
    if (!collection) return 0;

    if (selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO) {
      return !isImageOutput(collection.modelName)
        ? collection?.price +
            (nudifyEnabled ? NUDIFY_PRICE : 0) +
            (upscaleEnabled ? UPSCALE_PRICE : 0) +
            VIDEO_RESOLUTION_PRICE[videoResolution] +
            VIDEO_DURATION_PRICE[videoDuration]
        : collection?.price;
    } else {
      return (
        collection?.price +
        VIDEO_RESOLUTION_PRICE[videoResolution] +
        VIDEO_DURATION_PRICE[videoDuration]
      );
    }
  }, [
    collection,
    selectedTab,
    nudifyEnabled,
    upscaleEnabled,
    videoResolution,
    videoDuration,
  ]);

  const [favoriteBurstKey, setFavoriteBurstKey] = useState(0);

  const handleFavoriteToggle = () => {
    if (!isLoading && collection._id) {
      const willBeFavorited = !user.favoriteCollections.includes(
        collection._id,
      );
      if (willBeFavorited) {
        setFavoriteBurstKey((k) => k + 1);
      }
      dispatch(toggleToFavorite({ collectionId: collection._id }));
    }
  };

  const hideCollectionAction = () => {
    if (!collection?._id || hideCollectionPending) {
      return;
    }

    const willShowCollection = isCollectionHidden;
    const hide = !willShowCollection;

    dispatch(hideCollection({ collectionId: collection._id, hide }))
      .unwrap()
      .then(() => {
        setIsCollectionHiddenLocally(!isCollectionHidden);
        setConfirmationAction(null);
        toast.success(
          willShowCollection
            ? "Collection shown successfully"
            : "Collection hidden successfully",
        );
      })
      .catch((err) => {
        toast.error(
          err ||
            (willShowCollection
              ? "Failed to show collection"
              : "Failed to hide collection"),
        );
      });
  };

  const hideCollectionVipAction = () => {
    if (!collection?._id || hideCollectionVipPending) {
      return;
    }

    const willShowVipTag = isVipTagHiddenLocally;
    const hide = !willShowVipTag;

    dispatch(hideCollectionVip({ collectionId: collection._id, hide }))
      .unwrap()
      .then(() => {
        setIsVipTagHiddenLocally(!isVipTagHiddenLocally);
        setConfirmationAction(null);
        toast.success(
          willShowVipTag
            ? "VIP tag shown successfully"
            : "Collection hidden from VIP category successfully",
        );
      })
      .catch((err) => {
        toast.error(
          err ||
            (willShowVipTag
              ? "Failed to show VIP tag"
              : "Failed to hide collection from VIP"),
        );
      });
  };

  const handleConfirmVisibilityAction = () => {
    if (confirmationAction === "hide") {
      hideCollectionAction();
      return;
    }

    if (confirmationAction === "hideVip") {
      hideCollectionVipAction();
    }
  };

  // const handleCheckboxClick = () => {
  //   const newValue = !agreeTos;
  //   setAgreeTos(newValue);
  //   if (newValue) {
  //     setShowOutline(false);
  //   }
  // };

  const handleGenerateSuccess = ({ mediaUrl, mediaRef, successMessage }) => {
    toast.custom(() => (
      <GenerationToast
        successMessage={successMessage}
        label="View"
        onClick={() => {
          navigate("/mine");

          window.scrollTo({
            top: 0,
            left: 0,
            behavior: "instant",
          });
        }}
      />
    ));

    setIsExploding(true);

    setTimeout(() => {
      setIsExploding(false);

      if (mediaUrl && mediaRef?.current) {
        setFlyAnimation({
          isActive: true,
          imageUrl: mediaUrl,
          startPosition: mediaRef.current.getBoundingClientRect(),
          imageDimensions: {
            width: mediaRef.current.offsetWidth,
            height: mediaRef.current.offsetHeight,
          },
          onComplete: () => {
            setFlyAnimation({
              isActive: false,
              imageUrl: null,
              startPosition: null,
              imageDimensions: null,
              onComplete: null,
            });
            handlePostGenerate();
          },
        });
      } else {
        handlePostGenerate();
      }
    }, 850);
  };

  // const handleConfirmPermissionToggle = () => {
  //   setConfirmPermission((prev) => {
  //     const next = !prev;
  //     if (next) {
  //       localStorage.setItem(MEDIA_PERMISSION_KEY, "true");
  //       setPermissionError(false);
  //     } else {
  //       localStorage.removeItem(MEDIA_PERMISSION_KEY);
  //     }
  //     return next;
  //   });
  // };

  const onGenerateClick = () => {
    // if (!confirmPermission) {
    //   setPermissionError(true);
    //   setTimeout(() => setPermissionError(false), 400);
    //   toast.error("Please confirm you have permission to use this media");
    //   return;
    // }

    if ((localStorage.getItem("wasPromptModalOpen") ?? false) === false) {
      setIsEnterPromptModalOpen(true);
      localStorage.setItem("wasPromptModalOpen", true);
      return;
    }
    // if (!agreeTos) {
    //   setShouldShake(true);
    //   setShowOutline(true);
    //   setTimeout(() => setShouldShake(false), 400);
    //   return;
    // }

    // localStorage.setItem("tosAccepted", "true");

    if (selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO) {
      dispatch(
        generateCollection({
          image: uploadedImage?.blob
            ? {
                file: new File(
                  [uploadedImage.blob],
                  `cropped-${Date.now()}.jpg`,
                  { type: "image/jpeg" },
                ),
                url: uploadedImage.url,
              }
            : uploadedImage,
          customPrompt,
          negativePrompt,
          nudify: nudifyEnabled,
          upscale: upscaleEnabled,
          modelId: collection.model,
          resolution: videoResolution,
          seed: customSeed,
          videoDuration,
        }),
      )
        .unwrap()
        .then(() => {
          handleGenerateSuccess({
            mediaUrl: uploadedImage?.url,
            mediaRef: uploadedImageRef,
            successMessage: "Generation started...",
          });
        })
        .catch((err) => {
          toast.error(err);
        });
    } else {
      dispatch(
        generateCollection({
          video: uploadedVideo,
          customPrompt,
          negativePrompt,
          modelId: collection.model,
          resolution: videoResolution,
          seed: customSeed,
          videoDuration,
        }),
      )
        .unwrap()
        .then(() => {
          handleGenerateSuccess({
            mediaUrl: uploadedVideo?.url,
            mediaRef: uploadedVideoRef,
            successMessage: "Extending started...",
          });
        })
        .catch((err) => {
          toast.error(err);
        });
    }
  };

  const resetStates = () => {
    setUploadedImage(null);
    setUploadedVideo(null);
    setCustomPrompt("");
    setNegativePrompt("");
    setNudifyEnabled(false);
    setUpscaleEnabled(false);
    setShowPrompts(false);
    // setShouldShake(false);
    // setShowOutline(false);
    // setAgreeTos(false);
    setIsEnterPromptModalOpen(false);
  };

  const handlePostGenerate = () => {
    const alreadyGeneratedVideo = localStorage.getItem("alreadyGeneratedVideo");

    if (!alreadyGeneratedVideo) {
      localStorage.setItem("alreadyGeneratedVideo", true);
      navigate("/mine");

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    }

    resetStates();
    onClose();
  };

  useEffect(() => {
    setIsCollectionHiddenLocally(collection?.isPublic === false);
    setIsVipTagHiddenLocally(false);
  }, [collection?._id, collection?.isPublic]);

  // Prefill the custom prompt when the modal opens with an externally-supplied
  // prompt (e.g. from HomePage CreateInputBar's "Generate" button).
  useEffect(() => {
    if (isOpen && initialPrompt) {
      setCustomPrompt(initialPrompt);
      setShowPrompts(true);
    }
  }, [isOpen, initialPrompt]);

  useEffect(() => {
    if (!collection) {
      return;
    }

    if (collection.nudify) {
      setNudifyEnabled(true);
    }

    if (collection.upscale) {
      setUpscaleEnabled(true);
    }

    if (collection.input.video.url) {
      setSelectedTab(MODAL_CONTENT.EXTEND_VIDEO);
    } else {
      setSelectedTab(MODAL_CONTENT.IMAGE_TO_VIDEO);
    }
  }, [collection]);

  const renderGenerateContainer = () => {
    const tooltipText =
      selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO
        ? "The uploaded image will be used as the first frame of the generated video. For best results, match the original image. The AI follows basic physics - objects not in the input image are unlikely to appear in the generated video."
        : "Extending a video reuses its last frame as the starting point for the new generation, making the final video longer and seamless.";

    const buttonText =
      selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO ? "Generate" : "Extend";

    return (
      <>
        <StyledIconWrapper
          onClick={() => {
            setIsEnterPromptModalOpen(true);
            localStorage.setItem("wasPromptModalOpen", true);
          }}
        >
          <LottieIcon
            animationData={settingsAnimation}
            trigger="hover"
            strokeColor={"var(--color-brand-600)"}
            size={22}
          />
        </StyledIconWrapper>
        <ExplosionButton
          style={{
            flex: 1,
            width: "100%",
            minWidth: 0,
          }}
          disabled={generateLoading}
          onClick={onGenerateClick}
          isLoading={generateLoading}
          isExploding={isExploding}
          buttonStyle={{ height: 42, fontSize: 15 }}
        >
          {generateLoading ? (
            <GenerateLoader>
              <span>Generating</span>
              <GenerateLoaderDots>
                <span />
                <span />
                <span />
              </GenerateLoaderDots>
              <GenerateLoaderSparkle>💦</GenerateLoaderSparkle>
            </GenerateLoader>
          ) : (
            <>
              {buttonText}
              <GenerateDrop aria-hidden="true" />
              {Math.ceil(collectionPrice)}
            </>
          )}
        </ExplosionButton>
        <InfoTooltip
          selectedTab={selectedTab}
          collection={collection}
          tooltipText={tooltipText}
          isMobile={isMobile}
        />
      </>
    );
  };

  if (!user || !collection) {
    return null;
  }

  const showHideVipTagButton =
    canManageCollectionVisibility &&
    (hasVipTag(collection) || isVipTagHiddenLocally);
  const isCollectionHidden =
    isCollectionHiddenLocally || collection?.isPublic === false;
  const confirmationTitle =
    confirmationAction === "hideVip"
      ? isVipTagHiddenLocally
        ? "Show VIP Tag"
        : "Hide VIP Tag"
      : isCollectionHidden
        ? "Show Collection"
        : "Hide Collection";
  const confirmationMessage =
    confirmationAction === "hideVip"
      ? isVipTagHiddenLocally
        ? "Are you sure you want to show this collection in the VIP category?"
        : "Are you sure you want to remove this collection from the VIP category?"
      : isCollectionHidden
        ? "Are you sure you want to show this collection?"
        : "Are you sure you want to hide this collection?";
  const confirmationLoading =
    confirmationAction === "hideVip"
      ? hideCollectionVipPending
      : hideCollectionPending;

  return (
    <>
      <CropModal
        selectedImage={uploadedImage}
        onClose={() => setIsOpenCropModal(false)}
        onConfirm={(croppedImageData) => {
          setUploadedImage({
            file: new File(
              [croppedImageData.blob],
              `cropped-${Date.now()}.jpg`,
              { type: "image/jpeg" },
            ),
            url: croppedImageData.url,
            blob: croppedImageData.blob,
          });
        }}
        isOpen={isOpenCropModal}
      />
      <Modal
        isOpen={isOpen}
        onClose={() => {
          if (!isLoading) {
            resetStates();
            onClose();
          }
        }}
      >
        <ModalContainer $disableOverflow>
          <Header>
            <Title>{formatModelName(collection.name)}</Title>
            <RowIcons>
              <FavoriteIconWrapper
                type="button"
                style={{ zIndex: 1 }}
                onClick={handleFavoriteToggle}
                aria-pressed={user.favoriteCollections.includes(collection._id)}
                aria-label={
                  user.favoriteCollections.includes(collection._id)
                    ? "Remove from favorites"
                    : "Add to favorites"
                }
              >
                <FavoriteBurst
                  key={favoriteBurstKey}
                  $trigger={favoriteBurstKey > 0}
                />
                <FavoriteHeartOutline
                  $active={user.favoriteCollections.includes(collection._id)}
                >
                  <HeartOutlineIcon />
                </FavoriteHeartOutline>
                <FavoriteHeartFilled
                  $active={user.favoriteCollections.includes(collection._id)}
                  $animate={favoriteBurstKey > 0}
                >
                  <HeartFilledIcon />
                </FavoriteHeartFilled>
              </FavoriteIconWrapper>

              <IconWrapper
                onClick={() => {
                  if (!isLoading) {
                    resetStates();
                    onClose();
                  }
                }}
              >
                <CloseIcon />
              </IconWrapper>
            </RowIcons>
          </Header>

          <CategoryContainer>
            {!!collection.keywords &&
              collection.keywords.map((keyword, index) => {
                return (
                  <CategoryItem key={index}>
                    <CategoryText>{keyword}</CategoryText>
                  </CategoryItem>
                );
              })}
          </CategoryContainer>

          {canManageCollectionVisibility && (
            <AdminActionsContainer>
              <AdminActionButton
                onClick={() => setConfirmationAction("hide")}
                disabled={hideCollectionPending}
              >
                {hideCollectionPending
                  ? "Saving..."
                  : isCollectionHidden
                    ? "Show"
                    : "Hide"}
              </AdminActionButton>

              {showHideVipTagButton && (
                <AdminActionButton
                  onClick={() => setConfirmationAction("hideVip")}
                  disabled={hideCollectionVipPending}
                >
                  {hideCollectionVipPending
                    ? "Saving..."
                    : isVipTagHiddenLocally
                      ? "Show VIP tag"
                      : "Hide VIP tag"}
                </AdminActionButton>
              )}
            </AdminActionsContainer>
          )}

          {!isImageOutput(collection?.modelName) && (
            <TabsContainer $activeTab={selectedTab}>
              <Tab
                onClick={() => {
                  if (!isLoading) {
                    setSelectedTab(MODAL_CONTENT.IMAGE_TO_VIDEO);
                  }
                }}
                $selected={selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO}
              >
                <ImageIcon color="#fff" />
                <TabText
                  $selected={selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO}
                >
                  Image to{" "}
                  {isImageOutput(collection?.modelName) ? "Image" : "Video"}
                </TabText>
              </Tab>
              <Tab
                $disabled={isImageOutput(collection?.modelName)}
                onClick={() => {
                  if (!isLoading) {
                    setSelectedTab(MODAL_CONTENT.EXTEND_VIDEO);
                  }
                }}
                $selected={selectedTab === MODAL_CONTENT.EXTEND_VIDEO}
              >
                <VideoRecorderIcon color="#fff" />
                <TabText $selected={selectedTab === MODAL_CONTENT.EXTEND_VIDEO}>
                  Extend Video
                </TabText>
              </Tab>
            </TabsContainer>
          )}

          <Content>
            <Container>
              {selectedTab === MODAL_CONTENT.EXTEND_VIDEO ? (
                <ExtendVideo
                  uploadedVideo={uploadedVideo}
                  setUploadedVideo={setUploadedVideo}
                  uploadedVideoRef={uploadedVideoRef}
                  // handleCheckboxClick={handleCheckboxClick}
                  // agreeTos={agreeTos}
                  // showOutline={showOutline}
                  // shouldShake={shouldShake}
                  generateContainer={
                    <>
                      <EmptyDiv />
                      <GenerateContainer>
                        {renderGenerateContainer()}
                      </GenerateContainer>
                    </>
                  }
                />
              ) : (
                <ImageToVideo
                  uploadedImage={uploadedImage}
                  setUploadedImage={setUploadedImage}
                  setIsOpenCropModal={setIsOpenCropModal}
                  nudifyEnabled={nudifyEnabled}
                  setNudifyEnabled={setNudifyEnabled}
                  upscaleEnabled={upscaleEnabled}
                  setUpscaleEnabled={setUpscaleEnabled}
                  // handleCheckboxClick={handleCheckboxClick}
                  // agreeTos={agreeTos}
                  // showOutline={showOutline}
                  // shouldShake={shouldShake}
                  uploadedImageRef={uploadedImageRef}
                  generateContainer={
                    <GenerateContainer>
                      {renderGenerateContainer()}
                    </GenerateContainer>
                  }
                />
              )}
              <RightColumn>
                <SectionLabel>After</SectionLabel>
                <MemoizedRightContent
                  output={collection?.output}
                  input={collection?.input}
                />
              </RightColumn>
            </Container>
          </Content>
          {!isImageOutput(collection.modelName) && (
            <MobileCheckboxRow
              $visible={selectedTab !== MODAL_CONTENT.EXTEND_VIDEO}
            >
              <MobileToggleRow onClick={() => setNudifyEnabled(!nudifyEnabled)}>
                <NudifyText>Nudify before scene</NudifyText>
                <MobileToggleTrack $active={nudifyEnabled}>
                  <MobileToggleThumb $active={nudifyEnabled} />
                </MobileToggleTrack>
              </MobileToggleRow>
              <MobileToggleRow
                onClick={() => setUpscaleEnabled(!upscaleEnabled)}
              >
                <NudifyText>Upscale</NudifyText>
                <MobileToggleTrack $active={upscaleEnabled}>
                  <MobileToggleThumb $active={upscaleEnabled} />
                </MobileToggleTrack>
              </MobileToggleRow>
            </MobileCheckboxRow>
          )}

          <GenerateContainerMobile>
            {renderGenerateContainer()}
          </GenerateContainerMobile>

          {/* <ConfirmPermissionContainer
            $error={permissionError && !confirmPermission}
            onClick={handleConfirmPermissionToggle}
          >
            <LottieIcon
              animationData={checkboxAnimation}
              trigger="toggle-reverse"
              isActive={confirmPermission}
              speed={2}
              size={18}
              strokeColor={
                permissionError && !confirmPermission ? "#e5484d" : "#0474fb"
              }
            />
            <ConfirmPermissionText
              $checked={confirmPermission}
              $error={permissionError && !confirmPermission}
            >
              I confirm I have permission to use this media
            </ConfirmPermissionText>
          </ConfirmPermissionContainer> */}
          {/* <TermsCheckboxComponent
            mobile
            handleCheckboxClick={handleCheckboxClick}
            agreeTos={agreeTos}
            shouldShake={shouldShake}
            showOutline={showOutline}
            offMarginTop
          /> */}
        </ModalContainer>
      </Modal>
      {flyAnimation.isActive && (
        <FlyToCollection
          isActive={flyAnimation.isActive}
          imageUrl={flyAnimation.imageUrl}
          startPosition={flyAnimation.startPosition}
          imageDimensions={flyAnimation.imageDimensions}
          onComplete={() => {
            dispatch(triggerCollectionTabAnimation());

            // Call callback from ImageToVideo if provided
            if (flyAnimation.onComplete) {
              flyAnimation.onComplete();
            }

            setFlyAnimation({
              isActive: false,
              imageUrl: null,
              startPosition: null,
              imageDimensions: null,
              onComplete: null,
            });
          }}
        />
      )}
      <EnterPromptModal
        isOpen={isEnterPromptModalOpen}
        onClose={() => setIsEnterPromptModalOpen(false)}
        customPrompt={customPrompt}
        setCustomPrompt={setCustomPrompt}
        negativePrompt={negativePrompt}
        setNegativePrompt={setNegativePrompt}
        videoResolution={videoResolution}
        setVideoResolution={setVideoResolution}
        enableVideoQuality={!isImageOutput(collection.modelName)}
        modelPrice={
          selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO
            ? collection?.price + (nudifyEnabled ? NUDIFY_PRICE : 0)
            : collection.price
        }
        setCustomSeed={setCustomSeed}
        customSeed={customSeed}
        collectionSeed={collection?.seed}
        videoDuration={videoDuration}
        setVideoDuration={setVideoDuration}
        setShowPrompts={setShowPrompts}
        showPrompts={showPrompts}
        resetSeed={isOpen}
        isMinVideoLength8sec={collection?.output.videoDuration === 8}
        collectionId={collection?._id}
      />
      <ConfirmationModal
        isOpen={!!confirmationAction}
        onClose={() => setConfirmationAction(null)}
        onConfirm={handleConfirmVisibilityAction}
        title={confirmationTitle}
        message={confirmationMessage}
        confirmText="Confirm"
        cancelText="Cancel"
        confirmButtonType="danger"
        isLoading={confirmationLoading}
      />
    </>
  );
};

export default GenerateCollectionModal;
